import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import Sidebar from "../../components/common/Sidebar";
import Header from "../../components/common/Header";
import discountApi, { Discount } from "../../api/discount";

const Discounts: React.FC = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");
  const navigate = useNavigate();

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const response = await discountApi.getDiscounts({
        search: searchTerm,
        status: filterStatus,
      });
      setDiscounts(response.discounts || []);
    } catch (error) {
      console.error("Error fetching discounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (
    discountId: string,
    currentStatus: boolean,
  ) => {
    try {
      if (currentStatus) {
        await discountApi.deactivateDiscount(discountId);
      } else {
        await discountApi.activateDiscount(discountId);
      }

      // Update the local state
      setDiscounts((prev) =>
        prev.map((discount) =>
          discount._id === discountId
            ? { ...discount, isActive: !currentStatus }
            : discount,
        ),
      );
    } catch (error) {
      console.error("Error updating discount status:", error);
    }
  };

  const handleDelete = async (discountId: string) => {
    if (window.confirm("Are you sure you want to delete this discount?")) {
      try {
        await discountApi.deleteDiscount(discountId);
        setDiscounts((prev) =>
          prev.filter((discount) => discount._id !== discountId),
        );
      } catch (error) {
        console.error("Error deleting discount:", error);
      }
    }
  };

  // Filter discounts locally for better UX
  const filteredDiscounts = discounts.filter((discount) => {
    const matchesSearch =
      discount.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      discount.attachedTo.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && discount.isActive) ||
      (filterStatus === "inactive" && !discount.isActive);

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatExpiryDate = (dateString: string | null) => {
    if (!dateString) return "No expiry";
    return formatDate(dateString);
  };

  const getUsagePercentage = (usedBy: string[], maxUsage: number | null) => {
    if (!maxUsage) return "Unlimited";
    return `${usedBy.length}/${maxUsage} (${Math.round((usedBy.length / maxUsage) * 100)}%)`;
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar drawerWidth={250} />
        <div className="flex-1">
          <Header drawerWidth={250} />
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar drawerWidth={250} />
      <div className="flex-1">
        <Header drawerWidth={250} />
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6 mt-20">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Discounts</h1>
              <p className="text-gray-600">
                Manage discount codes and promotions
              </p>
            </div>
            <Link
              to="/discounts/create"
              className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Discount
            </Link>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by code or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="sm:w-48">
                <select
                  value={filterStatus}
                  onChange={(e) =>
                    setFilterStatus(
                      e.target.value as "all" | "active" | "inactive",
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Discounts Table */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type & Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expires
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDiscounts.map((discount) => (
                    <tr key={discount._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {discount.code}
                          </div>
                          <div className="text-sm text-gray-500">
                            {discount.attachedTo.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className="capitalize">
                            {discount.discountType}
                          </span>
                          <span className="ml-1">
                            {discount.discountType === "flat" ? "₹" : "%"}
                            {discount.value}
                          </span>
                        </div>
                        {discount.minOrderValue && (
                          <div className="text-xs text-gray-500">
                            Min: ₹{discount.minOrderValue}
                          </div>
                        )}
                        {discount.maxLimit && (
                          <div className="text-xs text-gray-500">
                            Max Limit: ₹{discount.maxLimit}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getUsagePercentage(discount.usedBy, discount.maxUsage)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatExpiryDate(discount.expiresAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            discount.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {discount.isActive ? "Active" : "Inactive"}
                        </span>
                        {discount.isPublic && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Public
                          </span>
                        )}
                        {discount.forInstant && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Instant Discount (Applicable to Kinnow Instant orders only)
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(discount.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() =>
                              navigate(`/discounts/${discount._id}`)
                            }
                            className="text-orange-600 hover:text-orange-900 p-1"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/discounts/${discount._id}/edit`)
                            }
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              handleStatusToggle(
                                discount._id,
                                discount.isActive,
                              )
                            }
                            className={`p-1 ${
                              discount.isActive
                                ? "text-red-600 hover:text-red-900"
                                : "text-green-600 hover:text-green-900"
                            }`}
                            title={
                              discount.isActive ? "Deactivate" : "Activate"
                            }
                          >
                            {discount.isActive ? (
                              <XCircleIcon className="h-4 w-4" />
                            ) : (
                              <CheckCircleIcon className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(discount._id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredDiscounts.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  {searchTerm || filterStatus !== "all"
                    ? "No discounts found matching your criteria."
                    : "No discounts created yet."}
                </div>
                {!searchTerm && filterStatus === "all" && (
                  <Link
                    to="/discounts/create"
                    className="mt-4 inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Discount
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Discounts;
